import { useEffect, useRef, useState } from "react";
import { AntiMetalButton } from "../components/ui/anti-metal-button";
import { HandwritingText } from "../components/ui/handwriting-text";
import "./AurelixAIHero.css";

/* ── Animated counter ── */
function useCounter(target, decimals = 0) {
  const [val, setVal] = useState(0);
  const triggered = useRef(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !triggered.current) {
        triggered.current = true;
        const steps = 70, dur = 1800;
        let i = 0;
        const tick = setInterval(() => {
          i++;
          const p = 1 - Math.pow(1 - i / steps, 3);
          const v = target * p;
          setVal(decimals > 0 ? parseFloat(v.toFixed(decimals)) : Math.floor(v));
          if (i >= steps) { clearInterval(tick); setVal(target); }
        }, dur / steps);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, decimals]);
  return [val, ref];
}

function StatCard({ prefix = "", target, suffix = "", decimals = 0, label, desc, bg, textColor, rotate = 0, index = 0 }) {
  const [val, ref] = useCounter(target, decimals);
  const display = decimals > 0 ? val.toFixed(decimals) : val;
  return (
    <div
      ref={ref}
      className="aai-scard"
      style={{ background: bg, color: textColor, transform: `rotate(${rotate}deg)`, animationDelay: `${0.7 + index * 0.08}s` }}
    >
      <div className="aai-scard-num">{prefix}{display}{suffix}</div>
      <div className="aai-scard-label">{label}</div>
      <div className="aai-scard-rule" style={{ background: textColor === '#fff' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)' }} />
      <div className="aai-scard-desc">{desc}</div>
    </div>
  );
}

export default function AurelixAIHero() {
  return (
    <section className="aai-section">
      <div className="aai-inner">

        {/* Eyebrow */}
        <div className="aai-eyebrow-wrap">
          <span className="aai-eyebrow-dot" />
          <span>Aurelix AI</span>
        </div>

        {/* Headline with handwriting */}
        <h1 className="aai-heading">
          The question isn't "How can we use AI?" The better question is{" "}
          <HandwritingText
            words={[
              "where is the friction?",
              "what slows you down?",
              "what can be automated?",
              "where does it compound?",
            ]}
            interval={3200}
            duration={1.5}
            height="1.1em"
            className="aai-hw-word"
          />
        </h1>

        {/* Subtext */}
        <p className="aai-subline">
          We identify repetitive processes, disconnected systems and opportunities for intelligence — and design solutions around the actual business problem.
        </p>

        {/* CTA row */}
        <div className="aai-cta-row">
          <AntiMetalButton
            as="a"
            href="#ai-services"
            accentFrom="#a855f7"
            accentTo="#7c3aed"
          >
            See How It Works
          </AntiMetalButton>
          <a href="#ai-contact" className="aai-link">
            Talk to the team
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

        {/* Stat cards */}
        <div className="aai-scards-wrap">
          <StatCard
            prefix="" target={50} suffix="+"
            decimals={0} label="Workflows Automated"
            desc="Across operations, marketing and support"
            bg="#a855f7" textColor="#fff"
            rotate={-4} index={0}
          />
          <StatCard
            prefix="" target={20} suffix="+"
            decimals={0} label="AI Agents Deployed"
            desc="Built around real business functions"
            bg="#f5c024" textColor="#0d0d0d"
            rotate={0} index={1}
          />
          <StatCard
            prefix="" target={80} suffix="%"
            decimals={0} label="Time Saved"
            desc="Avg. reduction in manual repetitive work"
            bg="#1a1a1a" textColor="#fff"
            rotate={4} index={2}
          />
        </div>

      </div>
    </section>
  );
}
